import { useTranslation } from 'react-i18next';

import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { useSelectionActions } from '@/feature/betslip-sbk/hooks/use-selection-actions';
import {
    AddSelectionConflictingError,
    AddSelectionMaxSelectionsError,
    SelectionParam,
} from '@/feature/betslip-sbk/types';
import { getFilteredSelections } from '@/feature/betslip-sbk/utils/betslip-add-selections-actions';
import { toSelectionParam } from '@/feature/betslip-sbk/utils/to-selection-param';

import { FeaturedBet } from '../types';

export const useFeaturedBetActions = () => {
    const { addSelection } = useSelectionActions();
    const addComboFeaturedBet = useSbkBetSlipStore(state => state.actions.addComboFeaturedBet);
    const addSgpFeaturedBet = useSbkBetSlipStore(state => state.actions.addSgpFeaturedBet);
    const addSgpPlusFeaturedBet = useSbkBetSlipStore(state => state.actions.addSgpPlusFeaturedBet);
    const removeFeaturedBet = useSbkBetSlipStore(state => state.actions.removeFeaturedBet);
    const toggleMultipleSelectionStatus = useSbkBetSlipStore(state => state.actions.toggleMultipleSelectionStatus);
    const selectionOrder = useSbkBetSlipStore(state => state.selectionOrder);
    const { showToast, showInfoSheet } = useAlerts();
    const { t } = useTranslation('betslip_sbk');

    const getSelectionParamFromFeaturedBet = (featuredBet: FeaturedBet): SelectionParam[] =>
        featuredBet.events.flatMap(event =>
            event.selections.map(selection =>
                toSelectionParam(
                    { ...selection.option, odds: selection.odds },
                    selection.market,
                    // TODO: get is_sgp_enabled from api
                    { ...event, is_sgp_enabled: true }
                )
            )
        );

    const addFeaturedBet = async (featuredBet: FeaturedBet) => {
        const selections = getSelectionParamFromFeaturedBet(featuredBet);
        try {
            switch (featuredBet.bet_type) {
                case 'SINGLE':
                    await addSelection(selections[0]);
                    break;
                case 'COMBO':
                    await addComboFeaturedBet(selections);
                    break;
                case 'SGP':
                    await addSgpFeaturedBet(selections, featuredBet.odds.sgp_odds ?? {});
                    break;
                case 'SGP+':
                    await addSgpPlusFeaturedBet(selections, featuredBet.odds.sgp_odds ?? {});
                    break;
            }
        } catch (e: unknown) {
            if (e instanceof AddSelectionConflictingError) {
                const filteredSelections = getFilteredSelections(selections, selectionOrder);
                const selectionIds = filteredSelections.map(selection => selection.option.id);
                showInfoSheet({
                    title: t('conflicting_selections_title'),
                    description: t('conflicting_selections_description'),
                    buttonLabel: t('conflicting_selections_primary_button'),
                    secondaryLabel: t('conflicting_selections_secondary_button'),
                    handlePress: () => {
                        toggleMultipleSelectionStatus(selectionIds);
                    },
                    handleSecondaryPress: () => {
                        removeFeaturedBet(selectionIds);
                    },
                });
            } else if (e instanceof AddSelectionMaxSelectionsError) {
                showToast({ message: t('betslip_maxed_out'), toastType: 'warning' });
            }
        }
    };

    return { addFeaturedBet, removeFeaturedBet };
};
